import React, { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import { siteInfo, aboutData } from "../data/mockData";

const FadeIn = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 35 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: "easeOut" }} className={className}>
      {children}
    </motion.div>
  );
};

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", company: "", subject: "", message: ""
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 4000);
    setFormData({ name: "", email: "", phone: "", company: "", subject: "", message: "" });
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[350px] flex items-center justify-center bg-[#1a1a1a]">
        <div className="absolute inset-0 bg-black/40" />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }} className="relative z-10 text-center px-6">
          <h1 className="text-4xl md:text-5xl lg:text-6xl text-white font-light"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Contact Us
          </h1>
          <p className="mt-4 text-white/60 text-sm tracking-[0.1em]"
            style={{ fontFamily: "'Inter', sans-serif" }}>
            We'd love to hear from you
          </p>
          <div className="h-[1px] w-16 bg-[#c8a96e] mx-auto mt-6" />
        </motion.div>
      </section>

      {/* Contact Info + Form */}
      <section className="py-24 bg-[#faf9f7]">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
            {/* Info */}
            <div className="lg:col-span-2">
              <FadeIn>
                <h3 className="text-[12px] tracking-[0.3em] uppercase text-[#c8a96e] mb-4"
                  style={{ fontFamily: "'Inter', sans-serif" }}>Get In Touch</h3>
                <h2 className="text-3xl text-[#1a1a1a] font-light"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Astron Diamond
                </h2>

                <div className="mt-8 space-y-6">
                  <div className="flex items-start gap-4">
                    <MapPin className="w-5 h-5 text-[#c8a96e] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[#333] text-sm font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>
                        Visit Us
                      </p>
                      <p className="text-[#888] text-sm mt-1" style={{ fontFamily: "'Inter', sans-serif" }}>
                        {siteInfo.address}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <Phone className="w-5 h-5 text-[#c8a96e] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[#333] text-sm font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>
                        Call Us
                      </p>
                      <a href={`tel:${siteInfo.phone}`}
                        className="text-[#888] text-sm mt-1 hover:text-[#c8a96e] transition-colors block"
                        style={{ fontFamily: "'Inter', sans-serif" }}>
                        {siteInfo.phone}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <Mail className="w-5 h-5 text-[#c8a96e] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[#333] text-sm font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>
                        Email Us
                      </p>
                      <a href={`mailto:${siteInfo.email}`}
                        className="text-[#888] text-sm mt-1 hover:text-[#c8a96e] transition-colors block"
                        style={{ fontFamily: "'Inter', sans-serif" }}>
                        {siteInfo.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <Clock className="w-5 h-5 text-[#c8a96e] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[#333] text-sm font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>
                        Business Hours
                      </p>
                      <p className="text-[#888] text-sm mt-1" style={{ fontFamily: "'Inter', sans-serif" }}>
                        Mon - Fri: 9:00 AM - 5:30 PM EST
                      </p>
                    </div>
                  </div>
                </div>

                {/* Quick CTA */}
                <div className="mt-12 pt-8 border-t border-[#eee]">
                  <p className="text-[#555] text-sm leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Prefer a direct conversation? Give us a call or send an email—our team typically responds within 24 hours.
                  </p>
                </div>
              </FadeIn>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <FadeIn delay={0.2}>
                <div className="bg-white p-8 md:p-10">
                  <h3 className="text-2xl text-[#1a1a1a] font-light mb-8"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    Send Us a Message
                  </h3>

                  {submitted && (
                    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
                      className="mb-6 p-4 bg-[#c8a96e]/10 border border-[#c8a96e]/30 text-[#c8a96e] text-sm"
                      style={{ fontFamily: "'Inter', sans-serif" }}>
                      Thank you! We'll get back to you shortly.
                    </motion.div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-[11px] tracking-[0.12em] uppercase text-[#999] mb-2"
                          style={{ fontFamily: "'Inter', sans-serif" }}>Full Name *</label>
                        <input type="text" required value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          className="w-full px-4 py-3 bg-[#faf9f7] border border-[#eee] text-sm text-[#333] focus:outline-none focus:border-[#c8a96e] transition-colors"
                          style={{ fontFamily: "'Inter', sans-serif" }} />
                      </div>
                      <div>
                        <label className="block text-[11px] tracking-[0.12em] uppercase text-[#999] mb-2"
                          style={{ fontFamily: "'Inter', sans-serif" }}>Email *</label>
                        <input type="email" required value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          className="w-full px-4 py-3 bg-[#faf9f7] border border-[#eee] text-sm text-[#333] focus:outline-none focus:border-[#c8a96e] transition-colors"
                          style={{ fontFamily: "'Inter', sans-serif" }} />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-[11px] tracking-[0.12em] uppercase text-[#999] mb-2"
                          style={{ fontFamily: "'Inter', sans-serif" }}>Phone</label>
                        <input type="tel" value={formData.phone}
                          onChange={(e) => setFormData({...formData, phone: e.target.value})}
                          className="w-full px-4 py-3 bg-[#faf9f7] border border-[#eee] text-sm text-[#333] focus:outline-none focus:border-[#c8a96e] transition-colors"
                          style={{ fontFamily: "'Inter', sans-serif" }} />
                      </div>
                      <div>
                        <label className="block text-[11px] tracking-[0.12em] uppercase text-[#999] mb-2"
                          style={{ fontFamily: "'Inter', sans-serif" }}>Company</label>
                        <input type="text" value={formData.company}
                          onChange={(e) => setFormData({...formData, company: e.target.value})}
                          className="w-full px-4 py-3 bg-[#faf9f7] border border-[#eee] text-sm text-[#333] focus:outline-none focus:border-[#c8a96e] transition-colors"
                          style={{ fontFamily: "'Inter', sans-serif" }} />
                      </div>
                    </div>
                    <div>
                      <label className="block text-[11px] tracking-[0.12em] uppercase text-[#999] mb-2"
                        style={{ fontFamily: "'Inter', sans-serif" }}>Subject *</label>
                      <input type="text" required value={formData.subject}
                        onChange={(e) => setFormData({...formData, subject: e.target.value})}
                        className="w-full px-4 py-3 bg-[#faf9f7] border border-[#eee] text-sm text-[#333] focus:outline-none focus:border-[#c8a96e] transition-colors"
                        style={{ fontFamily: "'Inter', sans-serif" }} />
                    </div>
                    <div>
                      <label className="block text-[11px] tracking-[0.12em] uppercase text-[#999] mb-2"
                        style={{ fontFamily: "'Inter', sans-serif" }}>Message *</label>
                      <textarea required rows={5} value={formData.message}
                        onChange={(e) => setFormData({...formData, message: e.target.value})}
                        className="w-full px-4 py-3 bg-[#faf9f7] border border-[#eee] text-sm text-[#333] focus:outline-none focus:border-[#c8a96e] transition-colors resize-none"
                        style={{ fontFamily: "'Inter', sans-serif" }} />
                    </div>
                    <button type="submit"
                      className="inline-flex items-center gap-2 px-8 py-3 bg-[#1a1a1a] text-white text-[12px] tracking-[0.15em] uppercase hover:bg-[#c8a96e] hover:text-[#1a1a1a] transition-all duration-500"
                      style={{ fontFamily: "'Inter', sans-serif" }}>
                      <Send className="w-4 h-4" />
                      Send Message
                    </button>
                  </form>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
